import { historyService } from './historyService';
import { memoryService } from './memoryService';
import { FandomConcept } from '../types';

export interface GraphNode {
  id: string;
  label: string;
  type: 'query' | 'concept';
  group?: string;
  frequency: number;
  lastAccessed: Date;
  metadata: {
    intent?: string;
    overview?: string;
    conceptType?: string;
  };
  x?: number;
  y?: number;
  fx?: number; // Fixed x position (for pinned nodes)
  fy?: number; // Fixed y position
}

export interface GraphLink {
  source: string;
  target: string;
  strength: number;
  type: 'query-concept' | 'concept-concept' | 'query-query';
}

export interface GraphData {
  nodes: GraphNode[];
  links: GraphLink[];
}

export class GraphDataService {
  /**
   * Build graph data from user's exploration history
   */
  async buildGraphData(): Promise<GraphData> {
    const nodes: Map<string, GraphNode> = new Map();
    const links: GraphLink[] = [];
    
    try {
      // Get user's exploration history
      const history = await historyService.getUserHistory(100); // Get more for richer graph
      
      if (!history || history.length === 0) {
        console.log('No history data available for user');
        return { nodes: [], links: [] };
      }
    
    // Process each exploration
    history.forEach((exploration) => {
      const queryId = `query-${exploration.data.query}`;
      const queryData = exploration.data;
      
      // Add query node
      if (!nodes.has(queryId)) {
        nodes.set(queryId, {
          id: queryId,
          label: queryData.query,
          type: 'query',
          frequency: 1,
          lastAccessed: new Date(exploration.created_at),
          metadata: {
            intent: queryData.snapshot.intent?.type,
            overview: queryData.snapshot.llmResponse.overview.substring(0, 200) + '...'
          }
        });
      } else {
        // Update frequency if we've seen this query before
        const node = nodes.get(queryId)!;
        node.frequency++;
        node.lastAccessed = new Date(exploration.created_at);
      }
      
      // Process concepts from this exploration
      queryData.snapshot.llmResponse.concepts.forEach((concept: FandomConcept) => {
        const conceptId = `concept-${concept.text}`;
        
        // Add concept node
        if (!nodes.has(conceptId)) {
          nodes.set(conceptId, {
            id: conceptId,
            label: concept.text,
            type: 'concept',
            frequency: 1,
            lastAccessed: new Date(exploration.created_at),
            metadata: {
              conceptType: concept.type
            }
          });
        } else {
          const node = nodes.get(conceptId)!;
          node.frequency++;
          node.lastAccessed = new Date(exploration.created_at);
        }
        
        // Add query-concept link
        const linkId = `${queryId}-${conceptId}`;
        const existingLink = links.find(l => 
          (l.source === queryId && l.target === conceptId) ||
          (l.source === conceptId && l.target === queryId)
        );
        
        if (!existingLink) {
          links.push({
            source: queryId,
            target: conceptId,
            strength: 1,
            type: 'query-concept'
          });
        }
      });
      
      // Create concept-concept links within the same exploration
      const concepts = queryData.snapshot.llmResponse.concepts;
      for (let i = 0; i < concepts.length; i++) {
        for (let j = i + 1; j < concepts.length; j++) {
          const concept1Id = `concept-${concepts[i].text}`;
          const concept2Id = `concept-${concepts[j].text}`;
          
          const existingLink = links.find(l =>
            (l.source === concept1Id && l.target === concept2Id) ||
            (l.source === concept2Id && l.target === concept1Id)
          );
          
          if (!existingLink) {
            links.push({
              source: concept1Id,
              target: concept2Id,
              strength: 0.5,
              type: 'concept-concept'
            });
          } else {
            existingLink.strength += 0.1; // Strengthen if concepts appear together again
          }
        }
      }
    });
    
    // Find query-query connections through shared concepts
    const queryNodes = Array.from(nodes.values()).filter(n => n.type === 'query');
    for (let i = 0; i < queryNodes.length; i++) {
      for (let j = i + 1; j < queryNodes.length; j++) {
        const query1 = queryNodes[i];
        const query2 = queryNodes[j];
        
        // Find shared concepts
        const query1Concepts = links
          .filter(l => l.source === query1.id && l.type === 'query-concept')
          .map(l => l.target);
        const query2Concepts = links
          .filter(l => l.source === query2.id && l.type === 'query-concept')
          .map(l => l.target);
        
        const sharedConcepts = query1Concepts.filter(c => query2Concepts.includes(c));
        
        if (sharedConcepts.length > 0) {
          links.push({
            source: query1.id,
            target: query2.id,
            strength: sharedConcepts.length * 0.3,
            type: 'query-query'
          });
        }
      }
    }
    
      return {
        nodes: Array.from(nodes.values()),
        links
      };
    } catch (error) {
      console.error('Error building graph data:', error);
      return { nodes: [], links: [] };
    }
  }
  
  /**
   * Get local graph around a specific node
   */
  async getLocalGraph(nodeId: string, depth: number = 2): Promise<GraphData> {
    const fullGraph = await this.buildGraphData();
    const localNodes = new Set<string>();
    const localLinks: GraphLink[] = [];
    
    // BFS to find nodes within depth
    const queue: { id: string; depth: number }[] = [{ id: nodeId, depth: 0 }];
    const visited = new Set<string>();
    
    while (queue.length > 0) {
      const { id, depth: currentDepth } = queue.shift()!;
      
      if (visited.has(id) || currentDepth > depth) continue;
      
      visited.add(id);
      localNodes.add(id);
      
      // Find connected nodes
      fullGraph.links.forEach(link => {
        if (link.source === id || link.target === id) {
          const otherId = link.source === id ? link.target : link.source;
          
          if (currentDepth < depth) {
            queue.push({ id: otherId, depth: currentDepth + 1 });
          }
          
          if (localNodes.has(otherId) || currentDepth < depth) {
            localLinks.push(link);
          }
        }
      });
    }
    
    return {
      nodes: fullGraph.nodes.filter(n => localNodes.has(n.id)),
      links: localLinks
    };
  }
  
  /**
   * Apply filters to graph data
   */
  filterGraph(
    graphData: GraphData,
    filters: {
      searchQuery?: string;
      showOrphans?: boolean;
      conceptTypes?: string[];
      minFrequency?: number;
    }
  ): GraphData {
    let { nodes, links } = graphData;
    
    // Filter by search query
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      nodes = nodes.filter(node => 
        node.label.toLowerCase().includes(query)
      );
    }
    
    // Filter by concept types
    if (filters.conceptTypes && filters.conceptTypes.length > 0) {
      nodes = nodes.filter(node => 
        node.type === 'query' || 
        filters.conceptTypes!.includes(node.metadata.conceptType || '')
      );
    }
    
    // Filter by frequency
    if (filters.minFrequency) {
      nodes = nodes.filter(node => node.frequency >= filters.minFrequency!);
    }
    
    // Update links to only include those between visible nodes
    const nodeIds = new Set(nodes.map(n => n.id));
    links = links.filter(link => 
      nodeIds.has(link.source) && nodeIds.has(link.target)
    );
    
    // Handle orphans
    if (!filters.showOrphans) {
      const connectedNodes = new Set<string>();
      links.forEach(link => {
        connectedNodes.add(link.source);
        connectedNodes.add(link.target);
      });
      
      nodes = nodes.filter(node => connectedNodes.has(node.id));
    }
    
    return { nodes, links };
  }
}

export const graphDataService = new GraphDataService();